import { useState, FormEvent, useRef, useEffect, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, Loader2, Info, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useAddressSuggest, SuggestOption } from '@/hooks/useAddressSuggest';
import {
  GeopositSuggestion,
  GroupedResult,
  groupSuggestions,
} from '@/lib/addressGrouping';
import { useLanguage } from '@/hooks/useLanguage';
import { FlagIcon } from '@/components/FlagIcon';

/* ─── Constants ─── */
const COUNTRIES = ['SE', 'NO', 'DK', 'FI'] as const;
type CountryCode = typeof COUNTRIES[number];

const COUNTRY_FLAG_CODES: Record<CountryCode, string> = {
  SE: 'sv',
  NO: 'no',
  DK: 'da',
  FI: 'fi',
};

/* ─── Autocomplete Dropdown ─── */
interface AutocompleteDropdownProps {
  suggestions: SuggestOption[];
  loading: boolean;
  visible: boolean;
  onSelect: (option: SuggestOption) => void;
  dropdownRef: RefObject<HTMLDivElement>;
}

function AutocompleteDropdown({ suggestions, loading, visible, onSelect, dropdownRef }: AutocompleteDropdownProps) {
  const { t } = useTranslation();

  const capped = suggestions.slice(0, 5);

  if (!visible || capped.length === 0) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden"
      style={{ minWidth: '260px' }}
    >
      <ul className="max-h-64 overflow-y-auto py-1">
        {capped.map((s, i) => (
          <li key={`${s.label}-${i}`}>
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between gap-3"
              onClick={() => onSelect(s)}
            >
              <span className="font-medium text-foreground truncate">{s.label}</span>
              {(s.postcode || s.locality) && (
                <span className="text-muted-foreground text-xs whitespace-nowrap flex-shrink-0">
                  {[s.postcode, s.locality].filter(Boolean).join(' ')}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      {loading && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-border text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('addressSearch.searching')}
        </div>
      )}
    </div>
  );
}

/* ─── Hook for dropdown open/close ─── */
function useDropdown(inputRef: RefObject<HTMLInputElement | null>) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  return { open, setOpen, dropdownRef };
}

/* ─── Country Selector ─── */
interface CountrySelectorProps {
  country: CountryCode;
  onChange: (country: CountryCode) => void;
}

function CountrySelector({ country, onChange }: CountrySelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 text-center">
      <Label className="text-sm font-medium">{t('addressSearch.selectCountry')}</Label>
      <ToggleGroup
        type="single"
        value={country}
        onValueChange={(val) => {
          if (val) onChange(val as CountryCode);
        }}
        className="justify-center flex-wrap"
      >
        {COUNTRIES.map((code) => (
          <ToggleGroupItem
            key={code}
            value={code}
            aria-label={t(`addressSearch.countries.${code}`)}
            className="gap-1 px-2.5 py-1.5 text-xs sm:text-sm sm:px-3 sm:py-2 data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground"
          >
            <FlagIcon code={COUNTRY_FLAG_CODES[code]} className="w-5 h-3.5" />
            {code}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

/* ─── Info Box ─── */
function InfoBox() {
  const { t } = useTranslation();
  return (
    <div className="bg-muted/50 rounded-lg p-3 sm:p-5 text-xs sm:text-sm text-muted-foreground">
      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 text-secondary flex-shrink-0" />
          <p>{t('addressSearch.infoText2')}</p>
        </div>
        <p>{t('addressSearch.infoText3')}</p>
      </div>
    </div>
  );
}

/* ─── Rate Limit Alert ─── */
function RateLimitAlert() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <Alert variant="destructive" className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 text-foreground">
      <AlertTriangle className="h-5 w-5 text-amber-600" />
      <AlertTitle className="text-amber-800 dark:text-amber-400 font-semibold">
        {t('addressSearch.searchLimitTitle')}
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm text-amber-900/80 dark:text-amber-300/80">
          {t('addressSearch.searchLimitMessage')}
        </p>
        <Link to={getLocalizedPath('/kontakt')}>
          <Button variant="outline" size="sm" className="border-amber-600 text-amber-700 hover:bg-amber-100 dark:border-amber-500 dark:text-amber-400 dark:hover:bg-amber-950/40">
            {t('addressSearch.contactUs')}
          </Button>
        </Link>
      </AlertDescription>
    </Alert>
  );
}

/* ─── Main Component ─── */
export default function AddressSearch() {
  const { t } = useTranslation();
  const [country, setCountry] = useState<CountryCode>('SE');
  const [results, setResults] = useState<GroupedResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  const countryLower = country.toLowerCase();

  // Three autocomplete instances with country
  const streetSuggest = useAddressSuggest('query', countryLower, 250, limitReached);
  const postcodeSuggest = useAddressSuggest('postcode', countryLower, 250, limitReached);
  const localitySuggest = useAddressSuggest('locality', countryLower, 250, limitReached);

  const streetInputRef = useRef<HTMLInputElement>(null);
  const postcodeInputRef = useRef<HTMLInputElement>(null);
  const localityInputRef = useRef<HTMLInputElement>(null);

  const streetDropdown = useDropdown(streetInputRef);
  const postcodeDropdown = useDropdown(postcodeInputRef);
  const localityDropdown = useDropdown(localityInputRef);

  // Country-specific labels
  const streetLabel = t(`addressSearch.countryLabels.${country}.street`);
  const postcodeLabel = t(`addressSearch.countryLabels.${country}.postcode`);
  const localityLabel = t(`addressSearch.countryLabels.${country}.locality`);

  // Country-specific column headers
  const colStreet = t(`addressSearch.countryColumns.${country}.street`);
  const colAdminCode = t(`addressSearch.countryColumns.${country}.adminCode`);
  const colCounty = t(`addressSearch.countryColumns.${country}.county`);
  const colMunicipality = t(`addressSearch.countryColumns.${country}.municipality`);

  // Show dropdowns when suggestions arrive
  useEffect(() => {
    if (streetSuggest.suggestions.length > 0 && streetSuggest.query.length >= 2) {
      streetDropdown.setOpen(true);
    }
  }, [streetSuggest.suggestions, streetSuggest.query]);

  useEffect(() => {
    if (postcodeSuggest.suggestions.length > 0 && postcodeSuggest.query.length >= 2) {
      postcodeDropdown.setOpen(true);
    }
  }, [postcodeSuggest.suggestions, postcodeSuggest.query]);

  useEffect(() => {
    if (localitySuggest.suggestions.length > 0 && localitySuggest.query.length >= 2) {
      localityDropdown.setOpen(true);
    }
  }, [localitySuggest.suggestions, localitySuggest.query]);

  const handleCountryChange = (newCountry: CountryCode) => {
    setCountry(newCountry);
    // Clear everything when country changes
    streetSuggest.clear();
    postcodeSuggest.clear();
    localitySuggest.clear();
    setResults([]);
    setError('');
    setHasSearched(false);
    streetDropdown.setOpen(false);
    postcodeDropdown.setOpen(false);
    localityDropdown.setOpen(false);
  };

  const performSearch = async (street: string, postcode: string, locality: string) => {
    const trimStreet = street.trim();
    const trimPostcode = postcode.trim();
    const trimLocality = locality.trim();

    if (!trimStreet && !trimPostcode && !trimLocality) return;

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      if (trimStreet) params.set('street', trimStreet);
      if (trimPostcode) params.set('postcode', trimPostcode);
      if (trimLocality) params.set('locality', trimLocality);
      params.set('country', countryLower);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/valid-search?${params.toString()}`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
        }
      );

      if (response.status === 429) {
        setLimitReached(true);
        return;
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const raw: GeopositSuggestion[] = data.suggestions || [];
      setResults(groupSuggestions(raw));
    } catch (err) {
      console.error('Search error:', err);
      setError(t('addressSearch.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e?: FormEvent) => {
    e?.preventDefault();
    streetDropdown.setOpen(false);
    postcodeDropdown.setOpen(false);
    localityDropdown.setOpen(false);
    performSearch(streetSuggest.query, postcodeSuggest.query, localitySuggest.query);
  };

  const handleSelectStreet = (option: SuggestOption) => {
    streetSuggest.setQuery(option.street || option.label);
    if (option.postcode) postcodeSuggest.setQuery(option.postcode);
    if (option.locality) localitySuggest.setQuery(option.locality);
    streetDropdown.setOpen(false);

    setTimeout(() => {
      performSearch(option.street || option.label, option.postcode || '', option.locality || '');
    }, 0);
  };

  const handleSelectPostcode = (option: SuggestOption) => {
    postcodeSuggest.setQuery(option.postcode || option.label);
    if (option.locality) localitySuggest.setQuery(option.locality);
    postcodeDropdown.setOpen(false);

    setTimeout(() => {
      performSearch(streetSuggest.query, option.postcode || option.label, option.locality || localitySuggest.query);
    }, 0);
  };

  const handleSelectLocality = (option: SuggestOption) => {
    localitySuggest.setQuery(option.locality || option.label);
    localityDropdown.setOpen(false);

    setTimeout(() => {
      performSearch(streetSuggest.query, postcodeSuggest.query, option.locality || option.label);
    }, 0);
  };

  const handleClear = () => {
    streetSuggest.clear();
    postcodeSuggest.clear();
    localitySuggest.clear();
    setResults([]);
    setError('');
    setHasSearched(false);
    streetDropdown.setOpen(false);
    postcodeDropdown.setOpen(false);
    localityDropdown.setOpen(false);
  };

  const hasInput = streetSuggest.query.trim() || postcodeSuggest.query.trim() || localitySuggest.query.trim();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Country selector */}
      <CountrySelector country={country} onChange={handleCountryChange} />

      {/* Search form */}
      <form onSubmit={handleSearch} className="space-y-3 md:space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 md:gap-4">
          {/* Street - 2 of 5 columns */}
          <div className="sm:col-span-2 relative">
            <Label htmlFor="street" className="text-xs sm:text-sm font-medium">
              {streetLabel}
            </Label>
            <Input
              ref={streetInputRef}
              id="street"
              type="text"
              autoComplete="off"
              value={streetSuggest.query}
              onChange={(e) => streetSuggest.setQuery(e.target.value)}
              onFocus={() => {
                if (!limitReached && streetSuggest.suggestions.length > 0 && streetSuggest.query.length >= 2) {
                  streetDropdown.setOpen(true);
                }
              }}
              placeholder={streetLabel}
              className="mt-1 h-10 sm:h-11 text-sm"
              disabled={limitReached}
            />
            <AutocompleteDropdown
              suggestions={streetSuggest.suggestions}
              loading={streetSuggest.loading}
              visible={streetDropdown.open}
              onSelect={handleSelectStreet}
              dropdownRef={streetDropdown.dropdownRef}
            />
          </div>

          {/* Postcode - 1 of 5 columns */}
          <div className="sm:col-span-1 relative" style={{ minWidth: '110px' }}>
            <Label htmlFor="postcode" className="text-xs sm:text-sm font-medium">
              {postcodeLabel}
            </Label>
            <Input
              ref={postcodeInputRef}
              id="postcode"
              type="text"
              autoComplete="off"
              value={postcodeSuggest.query}
              onChange={(e) => postcodeSuggest.setQuery(e.target.value)}
              onFocus={() => {
                if (!limitReached && postcodeSuggest.suggestions.length > 0 && postcodeSuggest.query.length >= 2) {
                  postcodeDropdown.setOpen(true);
                }
              }}
              placeholder={postcodeLabel}
              className="mt-1 h-10 sm:h-11 text-sm"
              disabled={limitReached}
            />
            <AutocompleteDropdown
              suggestions={postcodeSuggest.suggestions}
              loading={postcodeSuggest.loading}
              visible={postcodeDropdown.open}
              onSelect={handleSelectPostcode}
              dropdownRef={postcodeDropdown.dropdownRef}
            />
          </div>

          {/* Locality - 2 of 5 columns */}
          <div className="sm:col-span-2 relative">
            <Label htmlFor="locality" className="text-xs sm:text-sm font-medium">
              {localityLabel}
            </Label>
            <Input
              ref={localityInputRef}
              id="locality"
              type="text"
              autoComplete="off"
              value={localitySuggest.query}
              onChange={(e) => localitySuggest.setQuery(e.target.value)}
              onFocus={() => {
                if (!limitReached && localitySuggest.suggestions.length > 0 && localitySuggest.query.length >= 2) {
                  localityDropdown.setOpen(true);
                }
              }}
              placeholder={localityLabel}
              className="mt-1 h-10 sm:h-11 text-sm"
              disabled={limitReached}
            />
            <AutocompleteDropdown
              suggestions={localitySuggest.suggestions}
              loading={localitySuggest.loading}
              visible={localityDropdown.open}
              onSelect={handleSelectLocality}
              dropdownRef={localityDropdown.dropdownRef}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="text-xs sm:text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
          >
            {t('addressSearch.clearSearch')}
          </button>
          <Button
            type="submit"
            disabled={loading || !hasInput || limitReached}
            className="h-10 sm:h-11 px-4 sm:px-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground text-sm"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="hidden sm:inline">{t('addressSearch.searching')}</span>
                <span className="sm:hidden">{t('addressSearch.searching')}</span>
              </>
            ) : (
              <>
                <Search className="mr-1.5 sm:mr-2 h-4 w-4" />
                {t('addressSearch.searchButton')}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Rate limit message */}
      {limitReached && <RateLimitAlert />}

      {/* Info box – shown below form when no results, below table when results exist */}
      {!limitReached && (!hasSearched || results.length === 0) ? (
        <InfoBox />
      ) : null}

      {/* Results */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-secondary" />
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive text-sm">{error}</div>
      )}

      {!loading && !error && hasSearched && results.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          {t('addressSearch.noResults')}
        </div>
      )}

      {!loading && results.length > 0 && (
        <>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="max-h-[380px] sm:max-h-[480px] overflow-auto">
              <Table className="text-xs sm:text-sm">
                <TableHeader className="sticky top-0 z-10 bg-muted">
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold whitespace-nowrap">{colStreet}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap">{t('addressSearch.columns.from')}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap">{t('addressSearch.columns.to')}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap">{t('addressSearch.columns.postcode')}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap">{t('addressSearch.columns.locality')}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap">{colAdminCode}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap hidden sm:table-cell">{colCounty}</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap hidden sm:table-cell">{colMunicipality}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium whitespace-nowrap">{row.street}</TableCell>
                      <TableCell>{row.from}</TableCell>
                      <TableCell>{row.to}</TableCell>
                      <TableCell>{row.postcode}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.locality}</TableCell>
                      <TableCell>
                        <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
                          {row.lkfCode}
                        </code>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{row.county}</TableCell>
                      <TableCell className="hidden sm:table-cell">{row.municipality}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <InfoBox />
        </>
      )}
    </div>
  );
}